import { observer } from 'mobx-react-lite';
import React from 'react';

import { useStores } from '@src/Common/Hooks/useStores';
import { DumDTO, IMapCoordinate } from '@src/Domain/DUMEvents/DUMEvents.type';

import ButtonDefault from '@src/Components/Atoms/ButtonDefault/ButtonDefault';
import {
  CloseIcon,
  EventIcon,
  GameIcon,
  PlusIcon,
  ReadMoreIcon,
  RepoIcon
} from '@src/Components/Icons';

import { getFormatDate } from '@src/Common/Constants/getFormatDate';
import CustomLoading from '@src/Components/Atoms/CustomLoading/CustomLoading';
import AddEventModal from '../Modal/AddEventModal/AddEventModal';
import DefaultModal from '../Modal/DefaultModal/DefaultModal';
import styles from './DetailEventPopup.module.scss';
import { IconLeft } from './Icons/IconLeft';
import { IconRight } from './Icons/IconRight';
import useDetailEventPopup from './useDetailEventPopup';

type IDetailEventPopup = {
  eventData: IMapCoordinate;
  showAddBtn?: boolean;
  onClose?: () => void;
  onAddMore?: () => void;
};

const handleIconTitle = (type: string | undefined) => {
  if (type === 'dum' || type === 'report') return <EventIcon />;
  if (type === 'casefile') return <RepoIcon />;
  return <GameIcon />;
};

const getFileFormatFromUrl = (url: string) => {
  const filename = url.substring(url.lastIndexOf('/') + 1);
  const fileExtension = filename
    .substring(filename.lastIndexOf('.') + 1)
    .toLowerCase();
  const imageFormats = ['jpg', 'jpeg', 'png', 'gif', 'bmp'];
  const videoFormats = ['mp4', 'avi', 'mov', 'mkv', 'webm'];

  if (imageFormats.includes(fileExtension)) {
    return (
      <div className={styles['assets-wrapper']}>
        <img src={url} alt="assets-img" />
      </div>
    );
  }

  if (videoFormats.includes(fileExtension)) {
    return (
      <div className={styles['assets-wrapper']}>
        <video src={url} loop muted autoPlay playsInline controls />
      </div>
    );
  }

  return null;
};

const handleDescriptionParagraph = (
  detailEvent: DumDTO | undefined,
  typeData = ''
) => {
  if (typeData === 'dum') {
    return <p className={styles['Paragraph']}>{detailEvent?.description}</p>;
  }
  return (
    <div
      className={styles['ParagraphHtml']}
      dangerouslySetInnerHTML={{
        __html: detailEvent?.description as string
      }}
    ></div>
  );
};

const DetailEventPopup: React.FC<IDetailEventPopup> = observer(
  ({ eventData, onClose, showAddBtn = true }) => {
    const { DUMEventStore, FormStore } = useStores();
    const { data: detailEvent, status } = DUMEventStore.detailEventData;
    const {
      onPlusClick,
      typeEvent,
      handleClickBtn,
      checkCoordinate,
      selectedGlobalData,
      onOpenSource,
      onNextGlobalData,
      onPreviousGlobalData,
      anomalyRating
    } = useDetailEventPopup(eventData);

    return (
      <>
        <DefaultModal
          show={true}
          onClose={onClose}
          backgroundContent="transparent"
        >
          <div
            className={styles['ParentContainer']}
            data-testid="detail-event-popup"
          >
            <div className={styles['Container']}>
              <div className={styles['TopContainer']}>
                <div className={styles['LocationContainer']}>
                  <div className={styles['Location']}>
                    {eventData.city} /{' '}
                    <span>{`${checkCoordinate(
                      eventData.coordinates.length
                        ? eventData.coordinates[0]
                        : 0
                    )}, ${checkCoordinate(
                      eventData.coordinates.length
                        ? eventData.coordinates[1]
                        : 0
                    )}`}</span>
                  </div>
                  <div className={styles['RightContainer']}>
                    {showAddBtn ? (
                      <div className={styles['PlusIcon']} onClick={onPlusClick}>
                        <PlusIcon />
                      </div>
                    ) : null}
                    <div className={styles['CloseIcon']} onClick={onClose}>
                      <CloseIcon />
                    </div>
                  </div>
                </div>
                <div className={styles['TitleContainer']}>
                  <div className={styles['Logo']}>
                    {handleIconTitle(typeEvent)}
                  </div>
                  <div className={styles['Title']}>
                    {selectedGlobalData?.investigation}
                  </div>
                </div>
                <div className={styles['AccountDateContainer']}>
                  <div className={styles['DateContainer']}>
                    <div className={styles['Date']}>
                      {selectedGlobalData?.date
                        ? `Event Date: ${getFormatDate(
                            selectedGlobalData?.date,
                            false,
                            false,
                            '-'
                          )}`
                        : '-'}
                    </div>
                    {anomalyRating ? (
                      <>
                        <div>/</div>
                        <div className={styles['AnomalyRating']}>
                          Anomaly Rating: {anomalyRating.toFixed(2)}
                        </div>
                      </>
                    ) : null}
                  </div>
                  <div className={styles['Account']}>
                    {process.env.DISPLAY_AUTHOR === 'TRUE' && (
                      <>
                        <div className={styles['ImageContainer']}>
                          <img
                            src={`${
                              selectedGlobalData?.authorImageUrl ??
                              'assets/profile-mobile.png'
                            }`}
                            alt="profile-picture"
                          />
                        </div>
                        <p className={styles['Username']}>
                          {selectedGlobalData?.author}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles['ContentContainer']}>
                {status.isLoading ? (
                  <div className={styles['LoadingContainer']}>
                    <div className={styles['Loading']}>
                      <CustomLoading />
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className={`${styles['ParagraphContainer']} have-scroll`}
                      style={{
                        display: detailEvent?.description ? 'block' : 'none'
                      }}
                    >
                      <p className={styles['ParagraphTitle']}>Description</p>
                      {handleDescriptionParagraph(
                        detailEvent,
                        selectedGlobalData?.type
                      )}
                    </div>

                    {selectedGlobalData?.mediaUrl &&
                      getFileFormatFromUrl(selectedGlobalData?.mediaUrl)}
                  </>
                )}
              </div>
              <div className={styles['BottomContainer']}>
                <button
                  disabled={detailEvent?.product_reference === ''}
                  className={styles['BtnLink']}
                  onClick={onOpenSource}
                >
                  <span>Open the source</span>
                  <div className={styles['IconContainer']}>
                    <ReadMoreIcon />
                  </div>
                </button>
                <div>
                  {selectedGlobalData?.type === 'casefile' ? (
                    <ButtonDefault
                      onClick={handleClickBtn}
                      label={
                        selectedGlobalData?.type === 'casefile'
                          ? 'Continue Reading'
                          : 'Write Casefile'
                      }
                      icon={'edit'}
                      withIcon={false}
                      disabled={selectedGlobalData?.type !== 'casefile'}
                    />
                  ) : null}
                </div>
              </div>
              {eventData.hoverData.length > 1 ? (
                <div className={styles['FooterContainer']}>
                  <button onClick={onPreviousGlobalData}>
                    <IconLeft />
                  </button>
                  <div className={styles['TextPage']}>
                    {(DUMEventStore.selectedDetailDumGlobalIdIndex ?? 0) + 1} /{' '}
                    {eventData.hoverData.length}
                  </div>
                  <button onClick={onNextGlobalData}>
                    <IconRight />
                  </button>
                </div>
              ) : null}
            </div>
            {/* bottom button */}
            {showAddBtn ? (
              <div className={styles['BottomButtonContainer']}>
                <ButtonDefault
                  onClick={onPlusClick}
                  label="Add Event in this location"
                  withIcon={false}
                />
              </div>
            ) : null}
          </div>
        </DefaultModal>

        {FormStore.isDetailVisible ? (
          <AddEventModal
            show={true}
            isDetailVisible={FormStore.isDetailVisible}
            existingId={eventData.id}
            data={selectedGlobalData as any}
            closeDetailEvent={() => {
              FormStore.isDetailVisible = false;
            }}
            onClose={() => {
              FormStore.isDetailVisible = false;
            }}
          />
        ) : null}
      </>
    );
  }
);

export default DetailEventPopup;
